import { useState, useEffect, useMemo } from 'react'
import {
	EuiComboBox,
	EuiComboBoxOptionOption,
	EuiText
} from '@equipmentshare/ds2'
import { useVendorsQuery } from 'graphql/costcapture'
import { GRAPHQL_ENDPOINTS } from 'config/apollo-client'

type Vendor = {
	id: string,
	name: string,
}

type VendorSelectProps = {
	value?: string,
	onChange: ( vendorId: string | undefined ) => void,
	isInvalid?: boolean,
}

type VendorOption = EuiComboBoxOptionOption<string> & {
	value: string,
	vendor: Vendor,
}

export default function VendorSelect( {
	value,
	onChange,
	isInvalid = false
}: VendorSelectProps ) {
	const [ searchValue, setSearchValue ] = useState( '' )
	const [ selectedOptions, setSelectedOptions ] = useState<VendorOption[]>( [] )

	// Query vendors with search and CostCapture context
	const { data, loading, error } = useVendorsQuery( {
		variables: { search: searchValue },
		context: {
			endpoint: GRAPHQL_ENDPOINTS.COST_CAPTURE,
			debounceKey: 'vendorsQuery-debounce'
		},
		skip: false // Always fetch some initial data
	} )

	// Convert GraphQL data to combo box options
	const options = useMemo<VendorOption[]>( () => {
		if ( !data?.vendors?.edges ) {
			return []
		}

		return data.vendors.edges.map( ( edge ) => {
			if ( !edge ) {
				return null
			}
			const vendor = edge.node
			return {
				label: vendor.name,
				value: vendor.id,
				vendor
			}
		} ).filter( ( option ): option is VendorOption => option !== null )
	}, [ data ] )

	// Update selected options when external value changes
	useEffect( () => {
		if ( value ) {
			const option = options.find( ( opt ) => opt.value === value )
			if ( option ) {
				setSelectedOptions( [ option ] )
			}
		} else {
			setSelectedOptions( [] )
		}
	}, [ value, options ] )

	const onSearchChange = ( newSearchValue: string ) => {
		setSearchValue( newSearchValue )
	}

	const onSelectionChange = ( newSelectedOptions: EuiComboBoxOptionOption<string>[] ) => {
		// Type guard to check if option is VendorOption
		const isVendorOption = ( opt: EuiComboBoxOptionOption<string> ): opt is VendorOption => {
			return 'vendor' in opt && 'value' in opt
		}

		const validOptions = newSelectedOptions.filter( isVendorOption )
		setSelectedOptions( validOptions )

		if ( validOptions.length > 0 ) {
			const selectedVendor = validOptions[ 0 ].vendor
			onChange( selectedVendor.id )
		} else {
			onChange( undefined )
		}
	}

	if ( error ) {
		return (
			<EuiText size="s" color="danger">
				Error loading vendors. Please try again.
			</EuiText>
		)
	}

	return (
		<EuiComboBox
			placeholder="Search vendors..."
			options={options}
			selectedOptions={selectedOptions}
			onChange={onSelectionChange}
			onSearchChange={onSearchChange}
			singleSelection={{ asPlainText: true }}
			isLoading={loading}
			isInvalid={isInvalid}
			isClearable
		/>
	)
}
